import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogProps,
  DialogTitle,
  DialogTitleProps,
  Grid,
  IconButton,
  Slide,
  Typography,
} from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { TransitionProps } from '@material-ui/core/transitions/transition';
import CloseIcon from '@material-ui/icons/Close';
import React, { forwardRef, ReactElement, Ref } from 'react';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    dialogTitleRoot: {
      margin: 0,
    },
    dialogButtonAlone: {
      justifyContent: 'flex-end',
    },
    dialogContentContainer: {
      paddingTop: 0,
      // overflowX: 'hidden'
    },
  }),
);

export interface CustomDialogProps extends DialogProps {
  handleClose: () => void;
  handleConfirm?: () => void;
  containerStyle?: string;
  contentContainerStyle?: string;
  title?: string;
  titleStyle?: React.CSSProperties;
  subTitle?: string;
  customTitle?: JSX.Element;
  disabledOkButton?: boolean;
  okButtonLabel?: string;
  showOneButton?: boolean;
  showCloseButton?: boolean;
  hideDefaultButtons?: boolean;
  contentDividers?: boolean;
  CustomDialogActionsComponent?: React.ReactNode;
}

export interface CustomDialogTitleProps extends DialogTitleProps {
  showCloseButton?: boolean;
  customTitle?: JSX.Element;
  onClose?: () => void;
}

const Transition = forwardRef(function Transition(
  props: TransitionProps & { children?: ReactElement<any, any> },
  ref: Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const CustomDialogActions = (props: CustomDialogProps) => {
  const classes = useStyles();
  const {
    okButtonLabel,
    showOneButton,
    disabledOkButton,
    handleClose,
    handleConfirm,
  } = props;
  if (showOneButton) {
    return (
      <DialogActions
        style={{ justifyContent: 'flex-end', padding: 0, paddingTop: 8 }}
      >
        <Button
          onClick={handleClose}
          className={classes.dialogButtonAlone}
          variant="outlined"
          color="default"
        >
          {okButtonLabel ?? 'Fechar'}
        </Button>
      </DialogActions>
    );
  }
  return (
    <DialogActions
      style={{ justifyContent: 'flex-end', padding: 0, paddingTop: 8 }}
    >
      <Button onClick={handleClose} variant="outlined" color="default">
        Cancelar
      </Button>
      <Button
        disabled={disabledOkButton || false}
        onClick={handleConfirm}
        variant="contained"
        color="primary"
      >
        {okButtonLabel ?? 'Concluir'}
      </Button>
    </DialogActions>
  );
};

const CustomDialogTitle: React.FC<CustomDialogTitleProps> = props => {
  const classes = useStyles();

  const { children, showCloseButton, onClose, customTitle, ...other } = props;
  return (
    <DialogTitle
      disableTypography
      className={classes.dialogTitleRoot}
      {...other}
    >
      <Grid container wrap="nowrap" justify="space-between" alignItems="center">
        <Grid item xs={12}>
          <Typography variant="h6">
            {children}
            {customTitle}
          </Typography>
        </Grid>
        {showCloseButton && (
          <Grid item xs={'auto'}>
            <IconButton onClick={onClose}>
              <CloseIcon color="action" />
            </IconButton>
          </Grid>
        )}
      </Grid>
    </DialogTitle>
  );
};

const CustomDialog: React.FC<CustomDialogProps> = props => {
  const {
    CustomDialogActionsComponent,
    open,
    containerStyle,
    contentContainerStyle,
    maxWidth,
    title,
    titleStyle,
    subTitle,
    children,
    hideDefaultButtons,
    showCloseButton,
    contentDividers,
    handleClose,
    customTitle,
  } = props;
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      disableBackdropClick
      TransitionComponent={Transition}
      keepMounted
      fullWidth
      classes={{ paper: containerStyle }}
      maxWidth={maxWidth}
    >
      {(title || customTitle) && (
        <CustomDialogTitle
          onClose={handleClose}
          customTitle={customTitle}
          showCloseButton={showCloseButton}
          style={titleStyle}
        >
          {title}
        </CustomDialogTitle>
      )}
      <DialogContent
        className={contentContainerStyle}
        dividers={contentDividers}
      >
        {subTitle && <DialogContentText>{subTitle}</DialogContentText>}
        {children}
      </DialogContent>

      {!hideDefaultButtons && (
        <Box paddingX={3} paddingBottom={2} paddingTop={1}>
          {CustomDialogActionsComponent}
          {!CustomDialogActionsComponent && <CustomDialogActions {...props} />}
        </Box>
      )}
    </Dialog>
  );
};

export default CustomDialog;
